import { LEAD_SESSION_KEY } from '$lib/global/constants';

import { send } from '$lib/global/core/queue';
import { canDeliverEvents, getNowISO } from '$lib/global/tracking/helper';
import { getTrackingService } from '$lib/global/tracking/TrackingServiceInstance';
import { safeSessionStorage } from '$lib/global/tracking/trackingStorage';
import psProperties, { CONTACT_TRAIT_KEYS } from '$lib/global/tracking/v4.ps-properties';
import { embedOptions } from '$lib/Stores/stores';

import debug from 'debug';
import { flatten } from 'flat';
import { nanoid } from 'nanoid/non-secure';
import { get } from 'svelte/store';

import { getConsentStatus } from './consentGate';

const debugLog = debug('global:consent:lead');

/**
 * The first-party identify endpoint. Same origin as the funnel, so this is a plain POST with
 * no cookies, no credentials and no third party involved.
 */
const LEAD_ENDPOINT = '/ps-fnl-events/api/data/i';

/**
 * CAP-4169: deliver the lead even when the visitor rejected the cookie banner.
 *
 * CAP-4147 closed the consent gate on the whole event pipeline, which was correct for
 * tracking but also silently dropped form submissions — a visitor who typed their email
 * address and pressed submit never reached the customer's CRM.
 *
 * § 25 TDDDG governs *storing information in* and *accessing information already stored in*
 * the visitor's terminal equipment. It does not govern sending data the visitor themselves
 * just typed into a form. So this channel is deliberately built to touch no device storage
 * at all:
 *
 *   - it bypasses `window.analytics` entirely, so analytics.js is never loaded and no
 *     `ajs_*` cookie, localStorage or sessionStorage entry is created — and no third-party
 *     integration (Meta, Google, TikTok, …) sees the submission;
 *   - the ids it sends are ephemeral, minted in memory per page load and never persisted,
 *     so nothing links this visitor to a previous or later visit;
 *   - it does not use the `@segment/localstorage-retry` queue, because a failed delivery
 *     would write `perspective.<uuid>.queue` back to localStorage moments after
 *     `purgeTrackingArtifacts()` removed it;
 *   - it fires only on form submission, not on every answer click, so it captures the lead
 *     rather than the visitor's journey.
 *
 * Processing the lead itself rests on Art. 6(1)(b)/(f) GDPR — the visitor asked to be
 * contacted — not on consent.
 */

/**
 * The lead's `localId`: the key the backend dedups `Session` documents on.
 *
 * Persisted to sessionStorage (CAP-4250). CAP-4169 originally kept this in memory only,
 * reasoning that surviving a reload served our record keeping rather than the visitor. That
 * missed what a *missing* id actually costs: the backend resolves sessions by
 * `{companyId, campaignId, localId}`, so a fresh id misses the lookup and creates a **second
 * `Session`** — plus a second `SessionBillingRecord`, whose `_id` is the session id. One
 * person who reloads mid-funnel and submits again is therefore delivered to the customer's
 * CRM twice and billed as two visitors.
 *
 * This is terminal-equipment storage under § 25 TDDDG, so it rests on the "strictly
 * necessary" exemption rather than consent. The argument is duplicate suppression: being
 * contacted *once* is the service the visitor asked for, and not keeping duplicate records
 * of a data subject is Art. 5(1)(c) data minimisation — this stores less about them overall,
 * not more. Kept deliberately minimal to stay inside that argument: one opaque random id,
 * tab lifetime, scoped to a single campaign, carrying no traits and no behavioural data.
 *
 * A stable id also makes the reload lossless without persisting anything else. lemon-worker's
 * `updateSession` merges traits field by field (it builds `traits.<name>` keys), so a
 * post-reload delivery updates the same `Session` and answers given before the reload survive
 * server-side. That is why this module still reads the visitor's answers from memory only.
 */
let leadSessionId = '';

/** Guards against re-sending an identical payload (see the OTP note in `captureLead`). */
let lastDeliveredKey = '';

const getLeadSessionId = (campaignId?: string): string => {
    if (leadSessionId) {
        return leadSessionId;
    }

    // Nothing to scope the entry to. `captureLead()` drops deliveries without a campaign a
    // few lines further on anyway, so mint an id for this page load and store nothing.
    if (!campaignId) {
        leadSessionId = nanoid();

        return leadSessionId;
    }

    const key = `${LEAD_SESSION_KEY}${campaignId}`;
    const stored = safeSessionStorage.getItem(key);

    leadSessionId = stored || nanoid();

    if (!stored) {
        safeSessionStorage.setItem(key, leadSessionId);
    }

    return leadSessionId;
};

/**
 * Reduce a trait set to the part that decides whether two deliveries are the same lead.
 *
 * `ps_last_seen_at` is restamped by every `updateVisitorProperties()` call, so leaving it in
 * would mean two payloads that are otherwise byte-identical never compare equal and the
 * duplicate guard below could never fire. The other `ps_*_at` traits are set once on a real
 * state change (converted, completed, first seen) and must stay in the key.
 *
 * `TrackingSession.isIdentifyDuplicate()` drops analytics.js's regenerated messageId and
 * timestamp for the same reason.
 */
const toDeliveredKey = (traits: Record<string, unknown>): string => {
    const stable = { ...traits };

    delete stable[psProperties.lastSeenAt];

    return JSON.stringify(stable);
};

const hasContactData = (traits: Record<string, unknown>): boolean =>
    CONTACT_TRAIT_KEYS.some((key) => {
        const value = traits[key];

        return typeof value === 'string' ? value.trim() !== '' : Boolean(value);
    });

/**
 * Compose the lead's traits.
 *
 * `TrackingSession` accumulates answers, form values, `ps_converted` and `ps_last_seen_at` in
 * memory even while the gate is closed — only the persisting and the identify call are gated
 * — so the visitor's journey is already here. What is missing is everything
 * `initVisitorProperties()` would have seeded, because that runs off session creation, which
 * a rejected visitor never gets. Seed it from the URL and the in-memory entry page instead.
 *
 * Attribution survives rejection: reading `?utm_source=…` off the current URL is not an
 * access to device storage.
 *
 * Deliberately *not* seeded: `ps_timezone`. `Intl.DateTimeFormat().resolvedOptions()` reads
 * device configuration rather than the URL and is a well-known fingerprinting signal, which
 * puts it squarely in § 25 territory. Consented visitors still get it via the normal
 * pipeline; rejected ones do not.
 */
const buildTraits = (): Record<string, unknown> => {
    const service = getTrackingService();
    const { utm, custom, thirdParty } = service.queryParams;
    const entryPage = service.entryPage;

    return flatten({
        [psProperties.firstSeenAt]: service.firstSeenAt,
        [psProperties.startSlug]: entryPage?.pageSlug ?? '',
        [psProperties.startPage]: entryPage?.pageName ?? '',
        [psProperties.isEmbed]: get(embedOptions).isEmbed ?? false,
        ...utm,
        ...custom,
        ...thirdParty,
        ...service.visitorProperties,
    });
};

/**
 * Compose the pipeline context.
 *
 * `getEventContext()` already carries the campaign, company, version, workspace and page —
 * all funnel configuration, none of it read from the device — plus the ephemeral `userId` as
 * `anonymousId` / `globalId`. Only the session ids are overridden, because a rejected visitor
 * has no session and `sessionId` would be an empty string.
 */
const buildContext = () => {
    const { trackingTitles, ...perspective } = getTrackingService().getEventContext();
    // Scoped from the context rather than the service so this stays a leaf read — the same
    // campaignId the delivery is validated against below.
    const leadId = getLeadSessionId(perspective.campaignId);

    return {
        perspective: {
            ...perspective,
            perspectiveSessionId: leadId,
            localId: leadId,
        },
        // Human-readable field labels, so the lead renders with proper column names.
        dictionary: trackingTitles ?? {},
        // Both are already on the wire as User-Agent / Accept-Language request headers.
        userAgent: window.navigator.userAgent,
        locale: window.navigator.language,
    };
};

/**
 * Fire and forget.
 *
 * `sendBeacon` first: a submit is usually followed by a navigation (and sometimes an external
 * redirect), which can cancel an in-flight XHR — and unlike the consented path there is no
 * retry queue to catch that. `text/plain` keeps the request CORS-simple, so it never needs a
 * preflight, which a beacon cannot perform.
 */
const deliver = (payload: Record<string, unknown>): void => {
    const beacon = window.navigator?.sendBeacon;

    if (typeof beacon === 'function') {
        try {
            const body = new Blob([JSON.stringify(payload)], { type: 'text/plain' });

            if (beacon.call(window.navigator, LEAD_ENDPOINT, body)) {
                debugLog('-> lead delivered via sendBeacon');

                return;
            }
        } catch {
            // Beacon queue full or blocked — fall through to XHR.
        }
    }

    send(payload, LEAD_ENDPOINT, (err: { status: string } | null) => {
        if (err) {
            // Deliberately no queue.addItem() here: the retry queue persists to localStorage.
            console.warn(`Lead Capture Error: ${LEAD_ENDPOINT} - ${err.status}`);
        }
    });
};

/**
 * Deliver the lead for a visitor who declined tracking. No-ops for everyone else.
 *
 * Called from the form-submit boundary only (`v4.formSubmit` and the deferred conversion the
 * OTP flow triggers). Both can fire for one submission when phone/email verification defers
 * the conversion; the second call carries `ps_converted` and updates the same `Session`,
 * which mirrors the two identifies the consented pipeline sends. `lastDeliveredKey` drops the
 * genuinely redundant case where nothing changed between them.
 */
export const captureLead = (): void => {
    // Only 'denied'. While the gate is still 'pending' the visitor has not been asked yet —
    // and in practice cannot have submitted anything, because the banner is a full-screen
    // overlay that blocks the funnel until it is answered.
    if (getConsentStatus() !== 'denied') return;

    if (!canDeliverEvents()) return;

    const service = getTrackingService();

    // v3 is the legacy pipeline with a different payload contract and is not in scope.
    if (!service.isV4()) return;

    const traits = buildTraits();

    // No contact details means no lead yet — an intermediate step, not a submission worth
    // delivering. Keeps this from becoming journey tracking by the back door.
    if (!hasContactData(traits)) {
        debugLog('-> skipping lead: no contact data');

        return;
    }

    const context = buildContext();

    // The backend drops events without these, so don't bother sending one.
    if (!context.perspective.campaignId || !context.perspective.companyId) {
        debugLog('-> skipping lead: incomplete campaign context');

        return;
    }

    const deliveredKey = toDeliveredKey(traits);

    if (deliveredKey === lastDeliveredKey) {
        debugLog('-> skipping lead: identical to the last delivered payload');

        return;
    }

    lastDeliveredKey = deliveredKey;

    const now = getNowISO();

    debugLog('-> delivering lead without consent', traits);

    deliver({
        type: 'identify',
        userId: service.userId,
        anonymousId: service.userId,
        messageId: `perspective-lead-${nanoid()}`,
        timestamp: now,
        sentAt: now,
        traits,
        context,
    });
};

/** Test seam — resets module state between cases. */
export const resetLeadCapture = (): void => {
    leadSessionId = '';
    lastDeliveredKey = '';
};
